// ---------------------------------------------------------------------------
// EvaluationTemplatesService — HTTP-backed implementation against WorkflowSvc.
//
// Talks to /workspaces/{workspaceName}/evaluation/evaluation-templates on the
// configured API base URL. Response shapes mirror the C# DTOs in
// src/services/WorkflowSvc/Api/Contracts/EvaluationTemplates (camelCase JSON
// via ASP.NET Core defaults).
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import { AppError } from '../api/errors.js';
import type { CollectionResponse } from '../types/common.js';
import type {
  EvaluationTemplate,
  EvaluationTemplateVersion,
  PatchEvaluationTemplateBody,
  UpsertEvaluationTemplateBody,
} from '../types/evaluationTemplate.js';

import type { IEvaluationTemplatesService } from './evaluationTemplatesService.interface.js';
import {
  EvaluationTemplateAlreadyExistsError,
  EvaluationTemplateNotFoundError,
} from './evaluationTemplatesService.interface.js';

function basePath(workspaceName: string): string {
  return `workspaces/${encodeURIComponent(workspaceName)}/evaluation/evaluation-templates`;
}

function templatePath(workspaceName: string, name: string): string {
  return `${basePath(workspaceName)}/${encodeURIComponent(name)}`;
}

function isHttpStatus(err: unknown, status: number): boolean {
  return err instanceof AppError && err.statusCode === status;
}

export class EvaluationTemplatesService implements IEvaluationTemplatesService {
  constructor(private readonly client: IHttpClient) {}

  async list(workspaceName: string): Promise<EvaluationTemplate[]> {
    const { data } = await this.client.get<
      CollectionResponse<EvaluationTemplate>
    >(basePath(workspaceName));
    return data.value;
  }

  async get(workspaceName: string, name: string): Promise<EvaluationTemplate> {
    try {
      const { data } = await this.client.get<EvaluationTemplate>(
        templatePath(workspaceName, name),
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async create(
    workspaceName: string,
    name: string,
    body: UpsertEvaluationTemplateBody,
  ): Promise<EvaluationTemplate> {
    try {
      const { data } = await this.client.put<EvaluationTemplate>(
        templatePath(workspaceName, name),
        body,
        { headers: { 'If-None-Match': '*' } },
      );
      return data;
    } catch (err) {
      // The server returns 409 on a concurrent create collision; 412 if the
      // If-None-Match precondition fails because the template already exists.
      if (isHttpStatus(err, 409) || isHttpStatus(err, 412)) {
        throw new EvaluationTemplateAlreadyExistsError(workspaceName, name);
      }
      throw err;
    }
  }

  async replace(
    workspaceName: string,
    name: string,
    body: UpsertEvaluationTemplateBody,
  ): Promise<EvaluationTemplate> {
    const { data } = await this.client.put<EvaluationTemplate>(
      templatePath(workspaceName, name),
      body,
    );
    return data;
  }

  async patch(
    workspaceName: string,
    name: string,
    body: PatchEvaluationTemplateBody,
  ): Promise<EvaluationTemplate> {
    try {
      const { data } = await this.client.patch<EvaluationTemplate>(
        templatePath(workspaceName, name),
        body,
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async delete(workspaceName: string, name: string): Promise<void> {
    try {
      await this.client.delete<void>(templatePath(workspaceName, name));
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async snapshot(
    workspaceName: string,
    name: string,
  ): Promise<EvaluationTemplateVersion> {
    try {
      const { data } = await this.client.post<EvaluationTemplateVersion>(
        `${templatePath(workspaceName, name)}:snapshot`,
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async listVersions(
    workspaceName: string,
    name: string,
  ): Promise<EvaluationTemplateVersion[]> {
    try {
      const { data } = await this.client.get<
        CollectionResponse<EvaluationTemplateVersion>
      >(`${templatePath(workspaceName, name)}/versions`);
      return data.value;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }

  async getVersion(
    workspaceName: string,
    name: string,
    version: number,
  ): Promise<EvaluationTemplateVersion> {
    try {
      const { data } = await this.client.get<EvaluationTemplateVersion>(
        `${templatePath(workspaceName, name)}/versions/${version}`,
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationTemplateNotFoundError(workspaceName, name);
      }
      throw err;
    }
  }
}
